import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header/header';
import SubscribeSection from '../components/Subscribe';
import FooterSection from '../components/Footer';
import { getPostsByCategory } from '../services/postService';

const SITE_URL = 'https://exploroff.com';
const PAGE_TITLE = 'Hiking & Outdoor Guides | ExplorOFF';
const PAGE_DESC = 'Browse our complete collection of hiking guides, trail tips, and outdoor adventure how-tos — from gear picks to wilderness safety, for all skill levels.';
const CANONICAL = `${SITE_URL}/hiking-guides`;

const CATEGORIES = [
  { key: 'hiking-basics', label: 'Hiking Basics' },
  { key: 'gear-prep',     label: 'Gear & Prep'   },
  { key: 'trail-guides',  label: 'Trail Guides'  },
];

const SLUG_CATEGORY = {
  'hiking-basics': new Set([
    'benefits-of-hiking', 'beginner-hiking-mistakes', 'day-hike-packing-checklist',
    'overnight-backpacking-checklist', 'leave-no-trace-principles', 'hiking-etiquette',
    'hiking-for-weight-loss', 'hiking-mental-health-benefits', 'solo-hiking-tips-women',
    'night-hiking-tips', 'winter-hiking-beginners', 'fall-hiking-tips', 'desert-hiking-tips',
    'best-time-to-hike', 'how-to-find-hiking-trails', 'how-to-plan-multi-day-hike',
    'hiking-with-a-dog-guide', 'how-to-use-hiking-poles-correctly', 'how-to-make-hiking-stick',
    'how-to-plan-sustainable-hiking-trip', 'how-to-protect-yourself-from-wildlife-while-hiking',
    'how-to-train-for-high-altitude-hiking', 'how-to-prevent-blisters-when-hiking',
    'how-to-lace-hiking-boots', 'hiking-terms-glossary', 'how-long-to-hike-a-mile',
    'how-to-stretch-after-hiking', 'how-to-read-trail-markers', 'how-to-read-a-topographic-map',
    'what-to-wear-hiking-beginners', 'what-to-do-if-you-get-lost-hiking',
    'how-to-identify-poisonous-plants', 'how-to-navigate-without-gps',
    'how-to-prevent-dehydration-hiking', 'hypothermia-prevention-hiking',
    'lightning-safety-for-hikers', 'how-to-build-hiking-endurance',
    'how-to-hike-in-snow-and-ice', 'how-to-hike-in-the-rain', 'how-to-hike-with-kids',
  ]),
  'gear-prep': new Set([
    'best-hiking-backpacks', 'best-hiking-boots', 'best-trekking-poles', 'hiking-gps-apps',
    'best-hiking-headlamp', 'how-to-layer-for-hiking', 'sun-protection-for-hikers',
    'how-to-dress-for-hiking', 'best-hiking-snacks', 'what-to-eat-before-a-long-hike',
    'how-to-pack-food-backpacking', 'best-backpacking-meals', 'how-much-water-to-drink-hiking',
    'best-hiking-water-filters', 'how-to-find-eco-friendly-hiking-gear',
    'how-to-pack-hiking-backpack-diagram', 'best-hiking-daypack', 'best-headlamps',
    'best-layering-system', 'hiking-fitness-training-plan', 'how-to-choose-hiking-boots-vs-shoes',
    'how-to-waterproof-hiking-boots', 'how-to-sleep-comfortably-backpacking',
    'how-to-hang-a-bear-bag', 'how-to-treat-blisters-on-trail', 'how-to-stay-warm-on-cold-hikes',
    'how-to-hike-in-extreme-heat', 'how-to-purify-water-hiking', 'how-to-set-up-a-tent',
    'best-hiking-apps', 'how-to-get-a-backcountry-permit', 'best-backpacking-stove',
    'how-to-choose-a-hiking-backpack', 'hiking-first-aid-kit-essentials',
  ]),
  'trail-guides': new Set([
    'zion-narrows-hiking-guide', 'angel-landing-hike-guide', 'half-dome-trail-guide',
    'lake-serene-trail-guide', 'rattlesnake-ledge-trail-guide', 'mount-si-hiking-guide',
    'columbia-river-gorge-hiking', 'enchantments-hiking-guide',
    'rocky-mountain-national-park-hikes', 'yosemite-valley-hiking', 'mount-rainier-day-hikes',
    'acadia-national-park-hiking', 'great-smoky-mountains-hiking', 'colorado-14ers-guide',
    'presidential-traverse-new-hampshire', 'backpacking-wind-river-range',
  ]),
};

// Static fallback posts — shown when Firestore doesn't have the document yet.
// Firestore wins for any slug it returns; these fill the gaps.
const STATIC_POSTS = [
  { id: 'how-to-waterproof-hiking-boots',   title: 'How to Waterproof Hiking Boots',                    coverImage: '/img/how-to-waterproof-hiking-boots.webp',                   excerpt: 'Keep your feet dry on wet trails with the right waterproofing treatment for leather and synthetic boots.',                                                    readTime: 6 },
  { id: 'desert-hiking-tips',               title: 'Desert Hiking Tips: Stay Safe and Cool',             coverImage: '/img/desert-hiking-tips.webp',                               excerpt: 'Extreme heat, limited water, and flash floods make desert hiking uniquely dangerous. Essential tips for staying safe and hydrated.',                          readTime: 7 },
  { id: 'how-to-identify-poisonous-plants', title: 'How to Identify Poisonous Plants While Hiking',      coverImage: '/img/how-to-identify-poisonous-plants.webp',                  excerpt: 'Poison ivy, poison oak, stinging nettle, and more. Learn to identify the most common hazardous plants on North American trails.',                             readTime: 8 },
  { id: 'how-to-navigate-without-gps',      title: 'How to Navigate Without GPS',                       coverImage: '/img/how-to-navigate-without-gps.webp',                      excerpt: 'Map and compass skills every hiker should know. Learn to read topo maps, take bearings, and find your way when technology fails.',                              readTime: 9 },
  { id: 'how-to-prevent-dehydration-hiking',title: 'How to Prevent Dehydration While Hiking',            coverImage: '/img/how-to-prevent-dehydration-hiking.webp',                 excerpt: 'Dehydration is the most common hiking emergency. Know how much to drink and how to recognize the early signs before it becomes dangerous.',                    readTime: 6 },
  { id: 'hypothermia-prevention-hiking',    title: 'Hypothermia Prevention for Hikers',                  coverImage: '/img/hypothermia-prevention-hiking.webp',                    excerpt: 'Hypothermia can happen on a 50°F day. Learn the warning signs, how to dress to prevent heat loss, and how to respond on the trail.',                          readTime: 7 },
  { id: 'lake-serene-trail-guide',          title: 'Lake Serene Trail Guide (Washington)',               coverImage: '/img/lake-serene-trail-guide.webp',                          excerpt: 'One of the most dramatic day hikes in the Cascades — a turquoise alpine lake beneath the sheer granite walls of Mount Index. 8 miles, 2,500 ft gain.',         readTime: 6 },
  { id: 'leave-no-trace-principles',        title: 'Leave No Trace: The 7 Principles Explained',         coverImage: '/img/leave-no-trace-principles.webp',                        excerpt: 'A practical guide to the seven Leave No Trace principles for every trail, campsite, and waterway.',                                                             readTime: 8 },
  { id: 'lightning-safety-for-hikers',      title: 'Lightning Safety for Hikers',                       coverImage: '/img/lightning-safety-for-hikers.webp',                      excerpt: 'Above treeline when storms roll in, lightning becomes a life-or-death risk. Know the 30/30 rule and what to do when you cannot get off the summit.',            readTime: 7 },
  { id: 'night-hiking-tips',                title: 'Night Hiking Tips: How to Hit the Trail After Dark', coverImage: '/img/night-hiking-tips.webp',                                excerpt: 'Night hiking opens up a completely different world — cooler temps, fewer crowds, and stunning starscapes. Essential gear and safety tips.',                     readTime: 7 },
  { id: 'overnight-backpacking-checklist',  title: 'Overnight Backpacking Checklist: Complete Gear List',coverImage: '/img/overnight-backpacking-gear-list.webp',                  excerpt: 'Everything you need for your first overnight backpacking trip — shelter, sleep systems, food, water filtration, and the ten essentials.',                      readTime: 10 },
  { id: 'what-to-do-if-you-get-lost-hiking',title: 'What to Do If You Get Lost Hiking',                  coverImage: '/img/what-to-do-if-lost-hiking.webp',                        excerpt: 'Staying calm is step one. The STOP method, signaling for rescue, shelter priorities, and how to avoid getting lost in the first place.',                       readTime: 7 },
  { id: 'what-to-eat-before-a-long-hike',   title: 'What to Eat Before a Long Hike',                    coverImage: '/img/what-to-eat-before-hiking.webp',                        excerpt: 'The right pre-hike meal boosts energy and prevents bonking at mile eight. What to eat, when to eat it, and what to avoid the morning of a big hike.',          readTime: 6 },
  { id: 'what-to-wear-hiking-beginners',    title: "What to Wear Hiking: A Beginner's Guide",            coverImage: '/img/what-to-wear-hiking.webp',                              excerpt: 'Cotton kills, layers save lives. A breakdown of what to wear hiking — base layers, mid layers, rain gear, footwear, and sun protection for every season.',     readTime: 8 },
  { id: 'how-much-water-to-drink-hiking',   title: 'How Much Water to Drink While Hiking',               coverImage: '/img/how-much-water-to-drink-hiking.webp',                   excerpt: 'A practical formula for calculating your water needs based on body weight, pace, temperature, and elevation.',                                                  readTime: 6 },
  { id: 'how-to-build-hiking-endurance',    title: 'How to Build Hiking Endurance',                      coverImage: '/img/how-to-build-hiking-endurance.webp',                    excerpt: 'A structured training plan for building the cardiovascular fitness, leg strength, and mental stamina that long hikes demand.',                                   readTime: 8 },
  { id: 'how-to-hike-in-extreme-heat',      title: 'How to Hike Safely in Extreme Heat',                 coverImage: '/img/how-to-hike-in-extreme-heat.webp',                      excerpt: 'When temperatures exceed 95°F, hiking becomes a medical risk. Timing, hydration, clothing, and emergency signs to watch for.',                                 readTime: 7 },
  { id: 'how-to-hike-in-snow-and-ice',      title: 'How to Hike in Snow and Ice',                        coverImage: '/img/how-to-hike-in-snow-and-ice.webp',                      excerpt: 'Microspikes, gaiters, avalanche awareness, and winter layering — everything you need to extend your hiking season safely into winter.',                        readTime: 8 },
  { id: 'how-to-hike-in-the-rain',          title: 'How to Hike in the Rain: Tips and Gear',             coverImage: '/img/how-to-hike-in-the-rain.webp',                          excerpt: "Rain doesn't have to end your hike. The right gear and layering strategy make wet-weather hiking not just manageable — but genuinely enjoyable.",               readTime: 7 },
  { id: 'how-to-hike-with-kids',            title: 'How to Hike with Kids: Family Hiking Tips',          coverImage: '/img/how-to-hike-with-kids.webp',                            excerpt: 'The right trail, the right snacks, and the right attitude transform hiking into a family tradition. Tips for kid-friendly hikes and keeping young hikers motivated.', readTime: 7 },
  { id: 'how-to-read-a-topographic-map',    title: 'How to Read a Topographic Map',                      coverImage: '/img/how-to-read-a-topographic-map.webp',                    excerpt: "Contour lines, scale, and elevation profiles aren't complicated once you know what to look for. A beginner-friendly guide to topo maps.",                       readTime: 8 },
  { id: 'how-to-protect-yourself-from-wildlife-while-hiking', title: 'How to Protect Yourself from Wildlife While Hiking', coverImage: '/img/how-to-protect-yourself-from-wildlife-while-hiking.webp', excerpt: 'Bears, mountain lions, snakes, and more. Know the protocols for safely sharing the trail with wild animals.', readTime: 8 },
  { id: 'how-to-use-hiking-poles-correctly',title: 'How to Use Hiking Poles Correctly',                   coverImage: '/img/how-to-use-hiking-poles-correctly.webp',                 excerpt: 'Improve balance, reduce knee strain, and hike smarter with proper trekking pole technique for beginners and experienced hikers.',                               readTime: 7 },
  { id: 'mount-si-hiking-guide',            title: 'Mount Si Hiking Guide (Washington)',                  coverImage: '/img/mount-si-hiking-guide.webp',                            excerpt: 'The most popular day hike in the Pacific Northwest — 8 miles, 3,150 ft gain, sweeping Cascade and valley views.',                                               readTime: 6 },
  { id: 'rattlesnake-ledge-trail-guide',    title: 'Rattlesnake Ledge Trail Guide (Washington)',          coverImage: '/img/rattlesnake-ledge-trail-guide.webp',                    excerpt: 'A short, steep climb to one of the best views near Seattle. 4 miles, 1,100 ft gain, overlooking Rattlesnake Lake.',                                              readTime: 5 },
  { id: 'sun-protection-for-hikers',        title: 'Sun Protection for Hikers',                          coverImage: '/img/sun-protection-for-hikers.webp',                        excerpt: 'UV exposure is significantly higher at elevation. A complete guide to sunscreen, sun-protective clothing, hats, and eyewear for hikers.',                       readTime: 6 },
  { id: 'enchantments-hiking-guide',        title: 'The Enchantments Hiking Guide (Washington)',          coverImage: '/img/enchantments-hiking-guide.webp',                        excerpt: "One of the most beautiful wilderness areas in the country. The full guide to the Core Zone traverse, permit lottery, and what to pack.",                        readTime: 10 },
  { id: 'zion-narrows-hiking-guide',        title: 'Zion Narrows Hiking Guide',                          coverImage: '/img/zion-narrows-hiking-guide.webp',                        excerpt: 'Hike through the Virgin River inside a 1,000-foot slot canyon. Permits, water levels, dry suits, and the top-down vs. bottom-up approach.',                    readTime: 8 },
  { id: 'hiking-first-aid-kit-essentials',  title: 'Hiking First Aid Kit Essentials',                    coverImage: '/img/hiking-first-aid-kit-essentials.webp',                  excerpt: 'A trail-ready first aid kit is more than bandages. The exact items to pack for blisters, sprains, cuts, bee stings, and trail emergencies.',                    readTime: 7 },
];

function getCategoryFromSlug(slug) {
  for (const [cat, slugSet] of Object.entries(SLUG_CATEGORY)) {
    if (slugSet.has(slug)) return cat;
  }
  return null;
}

function getQueryParam(key) {
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

const STATIC_IMAGE_MAP = {
  // Hiking Basics
  'benefits-of-hiking':                                '/img/hiking-mental-health-benefits.webp',
  'beginner-hiking-mistakes':                          '/img/best-beginner-hiking-mistakes.webp',
  'best-time-to-hike':                                 '/img/best-time-to-hike.webp',
  'day-hike-packing-checklist':                        '/img/day-hike-packing-checklist.webp',
  'desert-hiking-tips':                                '/img/desert-hiking-tips.webp',
  'fall-hiking-tips':                                  '/img/fall-hiking-tips.webp',
  'hiking-etiquette':                                  '/img/hiking-etiquette.webp',
  'hiking-for-weight-loss':                            '/img/hiking-for-weight-loss.webp',
  'hiking-mental-health-benefits':                     '/img/hiking-mental-health-benefits.webp',
  'hiking-terms-glossary':                             '/img/hiking-terms-glossary.webp',
  'hiking-with-a-dog-guide':                           '/img/hiking-with-a-dog-guide.webp',
  'how-long-to-hike-a-mile':                           '/img/how-long-to-hike-a-mile.webp',
  'how-to-build-hiking-endurance':                     '/img/how-to-build-hiking-endurance.webp',
  'how-to-dress-for-hiking-in-summer':                 '/img/how-to-dress-for-hiking-in-summer.webp',
  'how-to-find-dog-friendly-hiking-trails':            '/img/how-to-find-dog-friendly-hiking-trails.webp',
  'how-to-find-hiking-trails':                         '/img/how-to-find-good-hiking-trails.webp',
  'how-to-get-into-hiking':                            '/img/how-to-get-into-hiking.webp',
  'how-to-hike-alone-safely':                          '/img/how-to-hike-alone-safely.webp',
  'how-to-hike-downhill-without-knee-pain':            '/img/how-to-hike-downhill-without-knee-pain.webp',
  'how-to-hike-in-extreme-heat':                       '/img/how-to-hike-in-extreme-heat.webp',
  'how-to-hike-in-snow-and-ice':                       '/img/how-to-hike-in-snow-and-ice.webp',
  'how-to-hike-in-the-rain':                           '/img/how-to-hike-in-the-rain.webp',
  'how-to-hike-steep-terrain':                         '/img/how-to-hike-steep-terrain.webp',
  'how-to-hike-with-heavy-backpack':                   '/img/how-to-hike-with-heavy-backpack.webp',
  'how-to-hike-with-kids':                             '/img/how-to-hike-with-kids.webp',
  'how-to-identify-poisonous-plants':                  '/img/how-to-identify-poisonous-plants.webp',
  'how-to-lace-hiking-boots':                          '/img/how-to-lace-hiking-boots.webp',
  'how-to-make-hiking-stick':                          '/img/how-to-make-hiking-stick.webp',
  'how-to-navigate-without-gps':                       '/img/how-to-navigate-without-gps.webp',
  'how-to-plan-sustainable-hiking-trip':               '/img/how-to-plan-sustainable-hiking-trip.webp',
  'how-to-prevent-dehydration-hiking':                 '/img/how-to-prevent-dehydration-hiking.webp',
  'how-to-protect-yourself-from-wildlife-while-hiking':'/img/how-to-protect-yourself-from-wildlife-while-hiking.webp',
  'how-to-read-a-topographic-map':                     '/img/how-to-read-a-topographic-map.webp',
  'how-to-read-trail-markers':                         '/img/how-to-read-trail-markers.webp',
  'how-to-stretch-after-hiking':                       '/img/how-to-stretch-after-hiking.webp',
  'how-to-use-a-compass-hiking':                       '/img/how-to-use-a-compass-for-hiking.webp',
  'how-to-use-hiking-poles-correctly':                 '/img/how-to-use-hiking-poles-correctly.webp',
  'hypothermia-prevention-hiking':                     '/img/hypothermia-prevention-hiking.webp',
  'leave-no-trace-principles':                         '/img/leave-no-trace-principles.webp',
  'lightning-safety-for-hikers':                       '/img/lightning-safety-for-hikers.webp',
  'night-hiking-tips':                                 '/img/night-hiking-tips.webp',
  'overnight-backpacking-checklist':                   '/img/overnight-backpacking-gear-list.webp',
  'solo-hiking-tips-women':                            '/img/how-to-hike-alone-safely.webp',
  'what-to-do-if-you-get-lost-hiking':                 '/img/what-to-do-if-lost-hiking.webp',
  'what-to-wear-hiking-beginners':                     '/img/what-to-wear-hiking.webp',
  'why-is-cotton-bad-for-hiking':                      '/img/why-wool-socks-beat-cotton.webp',
  'winter-hiking-beginners':                           '/img/how-to-stay-warm-on-cold-hikes.webp',

  // Gear & Prep
  'best-headlamp-hiking':                              '/img/best-headlamp-hiking.webp',
  'best-headlamps':                                    '/img/best-headlamp-hiking.webp',
  'best-hiking-backpacks':                             '/img/how-to-choose-a-hiking-backpack.webp',
  'best-hiking-boots':                                 '/img/hiking-boots-vs-trail-running-shoes.webp',
  'best-hiking-headlamp':                              '/img/best-headlamp-hiking.webp',
  'best-hiking-water-filters':                         '/img/how-to-purify-water-hiking.webp',
  'best-layering-system':                              '/img/how-to-layer-for-hiking.webp',
  'hiking-gps-apps':                                   '/img/best-hiking-apps.webp',
  'how-to-dress-for-hiking':                           '/img/how-to-dress-for-hiking-in-summer.webp',
  'best-backpacking-meals':                            '/img/best-backpacking-meals.webp',
  'best-backpacking-stove':                            '/img/best-backpacking-stove.webp',
  'best-bear-canister':                                '/img/best-bear-canister-backpacking.webp',
  'best-headlamp-hiking':                              '/img/best-headlamp-hiking.webp',
  'best-hiking-apps':                                  '/img/best-hiking-apps.webp',
  'best-hiking-daypack':                               '/img/best-hiking-daypack.webp',
  'best-hiking-pants-men':                             '/img/best-hiking-pants-men.webp',
  'best-hiking-pants-women':                           '/img/best-hiking-pants-women.webp',
  'best-hiking-rain-jacket':                           '/img/best-hiking-rain-jacket.webp',
  'best-hiking-shoes-for-wide-feet':                   '/img/best-hiking-shoes-wide-feet.webp',
  'best-hiking-snacks':                                '/img/best-hiking-snacks.webp',
  'best-hiking-socks':                                 '/img/why-wool-socks-beat-cotton.webp',
  'best-hiking-watch':                                 '/img/best-hiking-watch.webp',
  'best-lightweight-backpacking-tent':                 '/img/best-lightweight-backpacking-tent.webp',
  'best-sleeping-bag-backpacking':                     '/img/best-sleeping-bag-backpacking.webp',
  'best-trekking-poles':                               '/img/best-trekking-poles.webp',
  'best-ultralight-backpacking-gear':                  '/img/best-ultralight-backpacking.webp',
  'best-water-filter-backpacking':                     '/img/how-to-purify-water-hiking.webp',
  'hiking-emergency-essentials':                       '/img/hiking-emergency-essentials.webp',
  'hiking-first-aid-kit-essentials':                   '/img/hiking-first-aid-kit-essentials.webp',
  'hiking-fitness-training-plan':                      '/img/hiking-fitness-training-plan.webp',
  'hiking-knee-pain-prevention':                       '/img/hiking-knee-pain-prevention.webp',
  'how-much-water-to-drink-hiking':                    '/img/how-much-water-to-drink-hiking.webp',
  'how-to-break-in-hiking-boots':                      '/img/how-to-break-in-hiking-boots.webp',
  'how-to-carry-camera-while-hiking':                  '/img/how-to-carry-camera-while-hiking.webp',
  'how-to-choose-a-hiking-backpack':                   '/img/how-to-choose-a-hiking-backpack.webp',
  'how-to-choose-hiking-boots-vs-shoes':               '/img/hiking-boots-vs-trail-running-shoes.webp',
  'how-to-find-eco-friendly-hiking-gear':              '/img/how-to-find-eco-friendly-hiking-gear.webp',
  'how-to-get-a-backcountry-permit':                   '/img/how-to-get-a-backcountry-permit.webp',
  'how-to-hang-a-bear-bag':                            '/img/how-to-hang-a-bear-bag.webp',
  'how-to-layer-for-hiking':                           '/img/how-to-layer-for-hiking.webp',
  'how-to-pack-food-backpacking':                      '/img/how-to-pack-food-backpacking.webp',
  'how-to-pack-hiking-backpack-diagram':               '/img/how-to-pack-hiking-backpack-diagram.webp',
  'how-to-photograph-nature-while-hiking':             '/img/how-to-carry-camera-while-hiking.webp',
  'how-to-plan-a-backpacking-trip':                    '/img/how-to-plan-a-backpacking-trip.webp',
  'how-to-plan-multi-day-hike':                        '/img/how-to-plan-multi-day-hike.webp',
  'how-to-prevent-altitude-sickness':                  '/img/how-to-prevent-altitude-sickness.webp',
  'how-to-prevent-blisters-when-hiking':               '/img/how-to-prevent-blisters-when-hiking/how-to-prevent-blisters-when-hiking.webp',
  'how-to-purify-water-hiking':                        '/img/how-to-purify-water-hiking.webp',
  'how-to-set-up-a-tent':                              '/img/how-to-set-up-a-tent.webp',
  'how-to-sleep-comfortably-backpacking':              '/img/how-to-sleep-comfortably-backpacking.webp',
  'how-to-stay-warm-on-cold-hikes':                    '/img/how-to-stay-warm-on-cold-hikes.webp',
  'how-to-train-for-high-altitude-hiking':             '/img/how-to-train-for-high-altitude-hiking.webp',
  'how-to-treat-blisters-on-trail':                    '/img/how-to-treat-blister-on-trail.webp',
  'how-to-use-bear-spray':                             '/img/how-to-use-bear-spray.webp',
  'how-to-waterproof-hiking-boots':                    '/img/how-to-waterproof-hiking-boots.webp',
  'sun-protection-for-hikers':                         '/img/sun-protection-for-hikers.webp',
  'what-to-eat-before-a-long-hike':                    '/img/what-to-eat-before-hiking.webp',

  // Trail Guides
  'acadia-national-park-hiking':                       '/img/acadia-national-park.webp',
  'big-sur-hiking':                                    '/img/columbia-river-gorge-hiking.webp',
  'crater-lake-rim-trail':                             '/img/lake-serene-trail-guide.webp',
  'death-valley-winter-hiking':                        '/img/desert-hiking-tips.webp',
  'grand-teton-day-hikes':                             '/img/yellowstone-national-park.webp',
  'guadalupe-mountains-hiking':                        '/img/desert-hiking-tips.webp',
  'haleakala-sunrise-hike':                            '/img/how-to-train-for-high-altitude-hiking.webp',
  'lassen-volcanic-day-hikes':                         '/img/enchantments-hiking-guide.webp',
  'mount-whitney-trail':                               '/img/how-to-prevent-altitude-sickness.webp',
  'adirondack-high-peaks':                             '/img/adirondack-high-peaks.webp',
  'angel-landing-hike-guide':                          '/img/angels-landing-hike-guide.webp',
  'backpacking-wind-river-range':                      '/img/backpacking-the-wind-river-range.webp',
  'colorado-14ers-guide':                              '/img/how-to-train-for-high-altitude-hiking.webp',
  'columbia-river-gorge-hiking':                       '/img/columbia-river-gorge-hiking.webp',
  'enchantments-hiking-guide':                         '/img/enchantments-hiking-guide.webp',
  'great-smoky-mountains-hiking':                      '/img/great-smoky-mountains-national-park.webp',
  'half-dome-trail-guide':                             '/img/half-dome-trail-guide.webp',
  'lake-serene-trail-guide':                           '/img/lake-serene-trail-guide.webp',
  'little-si-trail-guide':                             '/img/rattlesnake-ledge-trail-guide.webp',
  'mount-rainier-day-hikes':                           '/img/enchantments-hiking-guide.webp',
  'mount-si-hiking-guide':                             '/img/mount-si-hiking-guide.webp',
  'olympic-national-park-itinerary':                   '/img/enchantments-hiking-guide.webp',
  'presidential-traverse-new-hampshire':               '/img/how-to-hike-steep-terrain.webp',
  'rattlesnake-ledge-trail-guide':                     '/img/rattlesnake-ledge-trail-guide.webp',
  'rocky-mountain-national-park-hikes':                '/img/rocky-mountain-national-park.webp',
  'yosemite-valley-hiking':                            '/img/half-dome-trail-guide.webp',
  'zion-narrows-hiking-guide':                         '/img/zion-narrows-hiking-guide.webp',
};

class BlogCollectionPage extends Component {
  state = { posts: [], loading: true, selectedCategory: getQueryParam('category') };

  componentDidMount() {
    getPostsByCategory('hiking-guides')
      .then(firestorePosts => {
        const enriched = firestorePosts.map(p => ({
          ...p,
          coverImage: p.coverImage || STATIC_IMAGE_MAP[p.id] || '',
        }));
        const firestoreIds = new Set(firestorePosts.map(p => p.id));
        const staticOnly = STATIC_POSTS.filter(p => !firestoreIds.has(p.id));
        this.setState({ posts: [...enriched, ...staticOnly], loading: false });
      })
      .catch(() => this.setState({ loading: false }));
  }

  selectCategory(key) {
    this.setState({ selectedCategory: key });
    const url = key ? `${window.location.pathname}?category=${key}` : window.location.pathname;
    window.history.pushState({}, '', url);
  }

  render() {
    const { posts, loading, selectedCategory } = this.state;

    const visible = selectedCategory
      ? posts.filter(p => getCategoryFromSlug(p.id) === selectedCategory)
      : posts;

    const activeCat = CATEGORIES.find(c => c.key === selectedCategory);

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: PAGE_TITLE,
      description: PAGE_DESC,
      url: CANONICAL,
      publisher: {
        '@type': 'Organization',
        name: 'ExplorOFF',
        url: SITE_URL,
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/img/logo-color-1x.png` },
      },
    };

    return (
      <>
        <Helmet>
          <title>{PAGE_TITLE}</title>
          <meta name="description" content={PAGE_DESC} />
          <link rel="canonical" href={CANONICAL} />
          <meta property="og:title" content={PAGE_TITLE} />
          <meta property="og:description" content={PAGE_DESC} />
          <meta property="og:url" content={CANONICAL} />
          <meta property="og:type" content="website" />
          <meta property="og:image" content={`${SITE_URL}/img/how-to-use-hiking-poles-correctly/how-to-use-hiking-poles.webp`} />
          <meta name="twitter:card" content="summary_large_image" />
          <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        </Helmet>

        <Header />

        <div style={{ paddingTop: '72px' }}>
          <section style={{ padding: '64px 0 36px', textAlign: 'center' }}>
            <div className="container">
              <p style={{ textTransform: 'uppercase', letterSpacing: '2px', fontSize: '0.8rem', color: '#0a7a8f', marginBottom: '12px', fontWeight: '700' }}>
                ExplorOFF Blog
              </p>
              <h1 style={{ fontSize: '2.4rem', fontWeight: '800', marginBottom: '16px', color: '#004858' }}>
                Hiking &amp; Outdoor Guides
              </h1>
              <p style={{ fontSize: '1.1rem', color: '#555', maxWidth: '560px', margin: '0 auto' }}>
                Expert tips, gear advice, and trail know-how for your next adventure
              </p>
            </div>
          </section>

          {/* Category filter tabs */}
          <div className="xcategory-filter-bar">
            <div className="container">

              {/* Desktop: pill buttons */}
              <div className="xcategory-filter-row xcategory-desktop-only">
                <button
                  className={'xcategory-pill' + (!selectedCategory ? ' active' : '')}
                  onClick={() => this.selectCategory(null)}
                >
                  All Guides
                </button>
                <span className="xcategory-divider">|</span>
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.key}
                    className={'xcategory-pill' + (selectedCategory === cat.key ? ' active' : '')}
                    onClick={() => this.selectCategory(cat.key)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Mobile: select dropdown */}
              <div className="xcategory-mobile-only">
                <select
                  className="xcategory-select"
                  value={selectedCategory || ''}
                  onChange={e => this.selectCategory(e.target.value || null)}
                >
                  <option value="">All Guides</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat.key} value={cat.key}>{cat.label}</option>
                  ))}
                </select>
              </div>

            </div>
          </div>

          <section style={{ padding: '32px 0 80px', background: '#fff' }}>
            <div className="container">

              {selectedCategory && (
                <div className="xtag-filter-label">
                  <span>
                    Showing <strong>{visible.length}</strong> {visible.length === 1 ? 'guide' : 'guides'} in <strong>{activeCat ? activeCat.label : selectedCategory}</strong>
                  </span>
                  <div
                    className="xtag-filter-clear"
                    role="button"
                    onClick={() => this.selectCategory(null)}
                  >
                    <i className="ti-close" /> Clear filter
                  </div>
                </div>
              )}

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                  <div className="xpost-spinner" />
                </div>
              ) : (
                <div className="row">
                  {visible.map((post) => (
                    <div className="col-md-6 col-lg-4 mb-4 d-flex" key={post.id}>
                      <article className="xcollection-card">
                        <a href={`/hiking-guides/${post.id}`} className="xcollection-card-img-link" tabIndex="-1" aria-hidden="true">
                          {post.coverImage ? (
                            <img
                              src={post.coverImage}
                              alt={post.title}
                              className="xcollection-card-img"
                              loading="lazy"
                            />
                          ) : (
                            <div className="xcollection-card-img xcollection-card-img-placeholder">
                              <i className="ti-map" />
                            </div>
                          )}
                        </a>
                        <div className="xcollection-card-body">
                          {getCategoryFromSlug(post.id) && (
                            <div className="xcollection-card-tags">
                              <span
                                className={'xcollection-card-tag' + (selectedCategory === getCategoryFromSlug(post.id) ? ' active' : '')}
                                role="button"
                                onClick={() => this.selectCategory(getCategoryFromSlug(post.id))}
                              >
                                {CATEGORIES.find(c => c.key === getCategoryFromSlug(post.id))?.label || getCategoryFromSlug(post.id)}
                              </span>
                            </div>
                          )}
                          <h2 className="xcollection-card-title">
                            <a href={`/hiking-guides/${post.id}`} className="xcollection-card-title-link">{post.title}</a>
                          </h2>
                          <p className="xcollection-card-excerpt">{post.excerpt}</p>
                          <div className="xcollection-card-footer">
                            {post.readTime && (
                              <span className="xcollection-card-meta">
                                <i className="ti-timer" /> {post.readTime} min
                              </span>
                            )}
                            <a href={`/hiking-guides/${post.id}`} className="xcollection-card-link">
                              Read Guide →
                            </a>
                          </div>
                        </div>
                      </article>
                    </div>
                  ))}
                </div>
              )}

              {!loading && visible.length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#888' }}>
                  <p>No guides found{selectedCategory ? ` in this category` : ''}.</p>
                  {selectedCategory && (
                    <div
                      role="button"
                      onClick={() => this.selectCategory(null)}
                      style={{ color: '#004858', fontWeight: '700', cursor: 'pointer', marginTop: '8px' }}
                    >
                      Show all guides
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>

        <SubscribeSection />
        <FooterSection />
      </>
    );
  }
}

export default BlogCollectionPage;
